import { useState } from 'react';
import FishboneDiagram from './FishboneDiagram.jsx';

/**
 * Presentational modules for the RAPID v2 Diagnosis redesign (PRD §6.1–6.9).
 * All copy is seeded/derived; these components are pure render functions.
 */

export const DIAGNOSIS_SECTIONS = [
  { key: 'summary', label: 'Summary', headingId: 'diagnosis-heading-summary' },
  { key: 'driver', label: 'Driver Diagram', headingId: 'diagnosis-heading-driver' },
  { key: 'current-state', label: 'Current State', headingId: 'diagnosis-heading-current-state' },
  { key: 'root-cause', label: 'Root Cause', headingId: 'diagnosis-heading-root-cause' },
  { key: 'fishbone', label: 'Fishbone / Ishikawa', headingId: 'diagnosis-heading-fishbone' },
  { key: 'process', label: 'Process / SIPOC', headingId: 'diagnosis-heading-process' },
  { key: 'measures', label: 'Measures', headingId: 'diagnosis-heading-measures' },
  { key: 'ai-critique', label: 'AI Critique', headingId: 'diagnosis-heading-ai-critique' },
];

const STORY_SLUG = {
  Symptom: 'symptom',
  'Root Cause': 'root-cause',
  'System Friction': 'friction',
  Proof: 'proof',
  'Action Focus': 'action-focus',
};
const SIPOC_ORDER = ['Suppliers', 'Inputs', 'Process', 'Outputs', 'Customers'];
const PROCESS_STEP_SLUG = {
  'PROC-001': 'schedule-pha',
  'PROC-002': 'identify-imr',
  'PROC-003': 'route-followups',
  'PROC-004': 'reconcile',
};
const MEASURE_SLUG = {
  'MOS-001': 'virtual-pha',
  'MOS-002': 'imr-screening',
  'MOS-003': 'data-owner',
};
const CONVERSION_SLUG = ['rfi-data-issue', 'staffing-risk', 'timeline'];

export function SectionHeading({ headingId, eyebrow, title }) {
  return (
    <div className="rapid-section-heading diagnosis-section-heading">
      {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
      <h3 id={headingId} data-testid={headingId} tabIndex={-1}>
        {title}
      </h3>
    </div>
  );
}

export function DiagnosisSectionNav({ active, onSelect }) {
  const expanded = active === 'all';
  return (
    <nav className="diagnosis-section-nav" data-testid="diagnosis-section-nav">
      <div className="diagnosis-nav-tabs" role="tablist" aria-label="Diagnosis sections">
        {DIAGNOSIS_SECTIONS.map((section) => (
          <button
            key={section.key}
            type="button"
            role="tab"
            aria-selected={active === section.key}
            className={`diagnosis-nav-chip${active === section.key ? ' is-active' : ''}`}
            data-testid={`diagnosis-nav-${section.key}`}
            onClick={() => onSelect(section.key)}
          >
            {section.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        className={`diagnosis-nav-chip diagnosis-nav-expand${expanded ? ' is-active' : ''}`}
        data-testid="diagnosis-expand-all"
        aria-pressed={expanded}
        onClick={() => onSelect(expanded ? 'summary' : 'all')}
      >
        {expanded ? 'Collapse' : 'Expand all'}
      </button>
    </nav>
  );
}

export function DiagnosisSummaryPanel({ storyboard }) {
  const sb = storyboard || {};
  const arc = sb.storyArc?.length ? sb.storyArc : Object.keys(STORY_SLUG);
  const arcDetails = sb.arcDetails || {};
  const [activeStep, setActiveStep] = useState(sb.activeStep || arc[0]);
  return (
    <section className="rapid-panel diagnosis-summary-panel" data-testid="diagnosis-summary-panel">
      <div className="eyebrow">Diagnosis summary</div>
      <h3 id="diagnosis-heading-summary" data-testid="diagnosis-heading-summary" tabIndex={-1}>
        Diagnosis summary
      </h3>
      <p className="diagnosis-summary-statement">{sb.primaryStatement}</p>

      <div
        className="diagnosis-story-arc"
        data-testid="diagnosis-story-arc"
        role="tablist"
        aria-label="Diagnostic story arc"
      >
        {arc.map((step, index) => {
          const slug = STORY_SLUG[step] || step.toLowerCase().replace(/\s+/g, '-');
          const active = step === activeStep;
          return (
            <button
              key={step}
              type="button"
              role="tab"
              aria-selected={active}
              className={`diagnosis-story-step${active ? ' is-active' : ''}`}
              data-testid={`diagnosis-story-step-${slug}`}
              onClick={() => setActiveStep(step)}
            >
              <span className="diagnosis-story-step-index">{index + 1}</span>
              <span className="diagnosis-story-step-label">{step}</span>
            </button>
          );
        })}
      </div>

      {arcDetails[activeStep] ? (
        <p className="diagnosis-story-detail" data-testid="diagnosis-story-detail">
          {arcDetails[activeStep]}
        </p>
      ) : null}

      <div className="diagnosis-summary-grid">
        <div className="diagnosis-summary-block">
          <h4>Top findings</h4>
          <ul className="diagnosis-findings">
            {(sb.findings || []).slice(0, 3).map((finding) => (
              <li key={finding}>{finding}</li>
            ))}
          </ul>
        </div>
        <div className="diagnosis-summary-block">
          <h4>Top risks</h4>
          <ul className="diagnosis-risks">
            {(sb.risks || []).slice(0, 2).map((risk) => (
              <li key={risk}>{risk}</li>
            ))}
          </ul>
        </div>
        <div
          className="diagnosis-summary-block diagnosis-next-best-action-block"
          data-testid="diagnosis-next-best-action"
        >
          <h4>Next best action</h4>
          <p>{sb.nextBestAction}</p>
        </div>
      </div>

      {sb.label ? <span className="chip outline sm diagnosis-summary-tag">{sb.label}</span> : null}
    </section>
  );
}

export function CurrentStateCards({ cards, title }) {
  return (
    <section
      className="rapid-panel diagnosis-current-state-section"
      data-testid="diagnosis-current-state-section"
    >
      <SectionHeading
        headingId="diagnosis-heading-current-state"
        eyebrow="Current state"
        title={title || 'Current-state assessment'}
      />
      <div className="diagnosis-card-grid">
        {(cards || []).map((card) => (
          <article
            key={card.id}
            className="diagnosis-takeaway-card"
            data-testid={`current-state-card-${card.id}`}
          >
            <h4>{card.title}</h4>
            <p>{card.body}</p>
            {card.chip ? <span className="chip outline sm">{card.chip}</span> : null}
          </article>
        ))}
      </div>
      <div className="diagnosis-seed-note muted small">Draft — owner confirmation needed.</div>
    </section>
  );
}

export function FiveWhysTimeline({ items }) {
  return (
    <ol className="five-whys-timeline" data-testid="five-whys-timeline">
      {(items || []).map((item, index) => (
        <li key={item.id} className="five-whys-step" data-testid={`five-whys-step-${index + 1}`}>
          <span className="five-whys-index">{index + 1}</span>
          <div className="five-whys-body">
            <strong>{item.why}</strong>
            <p>{item.answer}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function RootCauseSection({ fiveWhys }) {
  return (
    <section
      className="rapid-panel diagnosis-root-cause-section"
      data-testid="diagnosis-root-cause-section"
    >
      <SectionHeading
        headingId="diagnosis-heading-root-cause"
        eyebrow="Root cause"
        title="Root cause"
      />
      <div className="diagnosis-five-whys-block">
        <h4>5 Whys</h4>
        <FiveWhysTimeline items={fiveWhys} />
      </div>
    </section>
  );
}

export function FishboneSection({ fishbone, fishboneEffect }) {
  return (
    <section
      className="rapid-panel diagnosis-fishbone-section"
      data-testid="diagnosis-fishbone-section"
    >
      <SectionHeading
        headingId="diagnosis-heading-fishbone"
        eyebrow="Root cause"
        title="Fishbone / Ishikawa"
      />
      <FishboneDiagram fishbone={fishbone} effect={fishboneEffect} />
    </section>
  );
}

export function SipocMatrix({ sipoc }) {
  return (
    <div className="sipoc-matrix" data-testid="sipoc-matrix">
      {SIPOC_ORDER.map((column) => (
        <div
          key={column}
          className="sipoc-column"
          data-testid={`sipoc-column-${column.toLowerCase()}`}
        >
          <div className="sipoc-column-head">{column}</div>
          <ul>
            {(sipoc?.[column] || []).map((value) => (
              <li key={value}>{value}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function ProcessStepper({ processMap }) {
  const steps = processMap?.steps || [];
  if (!steps.length) {
    return <p>Process map not available.</p>;
  }
  return (
    <ol className="process-map-stepper" data-testid="process-map-stepper">
      {steps.map((step, index) => (
        <li
          key={step.id}
          className="process-step"
          data-testid={`process-step-${PROCESS_STEP_SLUG[step.id] || step.id.toLowerCase()}`}
        >
          <span className="process-step-index">{index + 1}</span>
          <div className="process-step-body">
            <strong>{step.label}</strong>
            <p>{step.note}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export function ProcessSipocSection({ sipoc, processMap }) {
  return (
    <section
      className="rapid-panel diagnosis-process-section"
      data-testid="diagnosis-process-section"
    >
      <SectionHeading
        headingId="diagnosis-heading-process"
        eyebrow="Process map"
        title="Process / SIPOC"
      />
      <div className="diagnosis-process-layout">
        <div className="diagnosis-process-col">
          <h4>SIPOC</h4>
          <SipocMatrix sipoc={sipoc} />
        </div>
        <div className="diagnosis-process-col">
          <h4>{processMap?.title || 'Process map'}</h4>
          <ProcessStepper processMap={processMap} />
        </div>
      </div>
    </section>
  );
}

export function MeasuresPanel({ measures, bowler, onOpenMetrics }) {
  const list = measures || [];
  return (
    <section
      className="rapid-panel diagnosis-measures-section"
      data-testid="diagnosis-measures-section"
    >
      <SectionHeading
        headingId="diagnosis-heading-measures"
        eyebrow="Measures of success"
        title="Measures of success"
      />
      {list.length ? (
        <div className="diagnosis-measure-card-grid">
          {list.slice(0, 3).map((measure) => (
            <article
              key={measure.id}
              className="diagnosis-measure-card"
              data-testid={`diagnosis-measure-card-${MEASURE_SLUG[measure.id] || measure.id.toLowerCase()}`}
            >
              <h4>{measure.label}</h4>
              <div className="diagnosis-measure-stats">
                <span>
                  <span className="k">Baseline</span>
                  <span className="v">{measure.baseline}</span>
                </span>
                <span>
                  <span className="k">Current</span>
                  <span className="v">{measure.current}</span>
                </span>
                <span>
                  <span className="k">Target</span>
                  <span className="v">{measure.target}</span>
                </span>
              </div>
              <p className="muted small">{measure.note}</p>
            </article>
          ))}
        </div>
      ) : (
        <p>Measures of success are not available for this RAPID.</p>
      )}
      {bowler ? (
        <div className="diagnosis-bowler-summary" data-testid="diagnosis-bowler-summary">
          <span className="diagnosis-bowler-line">
            <strong>{bowler.metric}:</strong> Baseline {bowler.baseline}, Current {bowler.current},
            Target {bowler.fy26Target}.
          </span>
          <button
            type="button"
            className="btn ghost sm"
            data-testid="diagnosis-open-metrics-proof"
            onClick={onOpenMetrics}
          >
            Open full bowler proof
          </button>
        </div>
      ) : null}
    </section>
  );
}

export function AiCritiquePanel({ aiCritique }) {
  const ai = aiCritique || {};
  if (!ai.summary && !(ai.cautions || []).length) {
    return (
      <section
        className="rapid-panel diagnosis-ai-critique-section"
        data-testid="diagnosis-ai-critique-section"
      >
        <SectionHeading
          headingId="diagnosis-heading-ai-critique"
          eyebrow="AI critique"
          title="AI critique"
        />
        <p>Critique not available.</p>
      </section>
    );
  }
  return (
    <section
      className="rapid-panel diagnosis-ai-critique-section"
      data-testid="diagnosis-ai-critique-section"
    >
      <SectionHeading
        headingId="diagnosis-heading-ai-critique"
        eyebrow="AI critique"
        title="AI critique"
      />
      <p className="diagnosis-ai-summary">{ai.summary}</p>
      <div className="diagnosis-ai-grid">
        <div className="diagnosis-ai-cautions" data-testid="diagnosis-ai-cautions">
          <h4>Cautions</h4>
          <ul className="rapid-inline-list">
            {(ai.cautions || []).map((caution) => (
              <li key={caution}>{caution}</li>
            ))}
          </ul>
        </div>
        <div
          className="diagnosis-ai-recommended-conversions"
          data-testid="diagnosis-ai-recommended-conversions"
        >
          <h4>Recommended conversions</h4>
          <div className="diagnosis-conversion-cards">
            {(ai.recommendedConversions || []).map((conversion, index) => (
              <article
                key={conversion}
                className="diagnosis-conversion-card"
                data-testid={`diagnosis-ai-conversion-${CONVERSION_SLUG[index] || `c${index}`}`}
              >
                <p>{conversion}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
      <div className="diagnosis-seed-note muted small">
        Draft — AI suggestions require human acceptance.
      </div>
    </section>
  );
}

export function AutomationProofDrawer({ nodes }) {
  return (
    <details
      className="rapid-panel diagnosis-proof-drawer"
      data-testid="diagnosis-automation-proof"
    >
      <summary className="diagnosis-proof-summary" data-testid="diagnosis-automation-proof-toggle">
        <span className="diagnosis-proof-label">
          <span className="eyebrow">Technical proof</span>
          <span className="diagnosis-proof-helper muted small">
            Deterministic driver-node mappings and test-facing lineage used to verify the diagnosis.
          </span>
        </span>
      </summary>
      <ul
        className="rapid-record-list diagnosis-deterministic-node-map"
        data-testid="diagnosis-deterministic-node-map"
      >
        {(nodes || []).map((node) => (
          <li key={node.id}>
            <div className="row spread">
              <strong>{node.id}</strong>
              <span className="chip outline sm">
                {node.podLabel} · Tier {node.tier}
              </span>
            </div>
            <div className="muted small">{node.driver}</div>
            <p>{node.metric}</p>
          </li>
        ))}
      </ul>
    </details>
  );
}
